import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useId, useMemo } from "react";
import { useForm } from "react-hook-form";

import ControlledCombobox from "~/components/form/controlled-combobox";
import styles from "~/components/form/phone-input-form.module.scss";
import crossCountryUtils from "~/utils/cross-country-utils";

import country2dialcode from "../../../public/json/country2dialcode.json";

interface PhoneInputFormValue {
  country?: string;
  number?: string;
  autocompleteHandler?: string;
}

interface PhoneInputFormProps {
  maxLength?: number;
  onChange?: (e: Partial<PhoneInputFormValue>) => void;
  placeholder?: string;
  value: string;
  inputId?: string;
}

export default function PhoneInputForm({ value, onChange, placeholder, maxLength = 25, inputId }: PhoneInputFormProps) {
  const router = useRouter();
  const routerCountry = crossCountryUtils.getCurrentCountryCode(router).toLowerCase();
  const id = useId();
  const t = useTranslations();

  const prefixes = useMemo(() => {
    const _prefixes = Object.entries(country2dialcode).map(([key, value]) => ({
      value: key,
      label: `${key.toUpperCase()} ${value}`,
    }));
    const defaultPrefixOption = _prefixes.splice(
      _prefixes.findIndex((p) => p.value === routerCountry),
      1
    );
    _prefixes.unshift(defaultPrefixOption[0]);

    return _prefixes;
  }, [routerCountry]);

  const parsePrefix = (value?: string): string => {
    return (
      crossCountryUtils.getCountryFromPhonePrefix((value || "").split(" ")[0] || "", routerCountry) || prefixes[0].value
    );
  };

  const parsePhoneNumber = (value?: string): string => {
    return value?.split(" ")[1] ?? "";
  };

  const defaultValues: PhoneInputFormValue = (() => {
    const country = parsePrefix(value);
    const number = parsePhoneNumber(value) || "";
    return {
      country,
      number,
      autocompleteHandler: country && number ? value : "",
    } as PhoneInputFormValue;
  })();

  const { control, register, getValues, watch, reset } = useForm<PhoneInputFormValue>({
    shouldFocusError: false,
    defaultValues,
  });

  useEffect(() => {
    const subscription = watch((newValue) => {
      let isAutocomplete = !1;
      // Handle browser autocomplete
      if (
        newValue.autocompleteHandler &&
        defaultValues.autocompleteHandler !== newValue.autocompleteHandler &&
        /^\+\d{1,3}\s\d+/.test(newValue.autocompleteHandler)
      ) {
        newValue.country = parsePrefix(newValue.autocompleteHandler);
        newValue.number = parsePhoneNumber(newValue.autocompleteHandler);
        isAutocomplete = !0;
      }
      if (newValue.country !== defaultValues.country || newValue.number !== defaultValues.number) {
        onChange?.(newValue);
      }
    });
    reset(defaultValues);

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset, defaultValues.number, defaultValues.country]);

  return (
    <>
      <input
        autoComplete="_off"
        type="text"
        {...register("autocompleteHandler")}
        tabIndex={-1}
        style={{ position: "absolute", opacity: "0", pointerEvents: "none" }}
        id={inputId}
      />
      <div className={styles.prefixWrapper}>
        <div className={styles.iconWrapper}>
          <Image
            className={styles.prefixIcon}
            unoptimized
            width={16}
            height={16}
            aria-hidden
            alt=""
            src={`/next/assets/flags/${getValues().country || prefixes[0].value}.svg`}
          />
          <ControlledCombobox
            name="country"
            control={control}
            options={prefixes}
            hideInputBorder
            maxOptions={prefixes.length}
            customClass={styles.countryCombobox}
          />
        </div>
        <label className="sr-only" htmlFor={id}>
          {t("a11y.phoneNumber")}
        </label>
        <input
          id={id}
          autoComplete="_off"
          className={styles.numberInput}
          type="text"
          {...register("number")}
          maxLength={maxLength - 5}
          placeholder={placeholder}
        />
      </div>
    </>
  );
}
